import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";

import type { Page } from "../../api";
import { FlagChip } from "../../chips";
import { OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import { HATCH_CLASS, HATCH_STYLE, LandedLegend } from "../bars";
import type { PageProps } from "../index";
import { f2, PageHead } from "../kit";
import { byTotalDesc, landedLines, stackMax, UNKNOWN_DUTY_WIDTH, type LandedLine, type LandedRow } from "../landed";
import { landedExtra, pageFilter } from "../params";
import { VariantPage } from "../variant";

const share = (value: number, max: number) => `${(value / max) * 100}%`;

/** Raw material, duty and transport as one 18px bar; an unknown duty is a fixed hatched marker. */
export function Breakdown({ line, max }: { line: LandedLine; max: number }) {
  return (
    <div
      role="img"
      aria-label={`Raw ${f2(line.raw)}, duty ${line.duty === null ? "unknown" : f2(line.duty)}, transport ${f2(line.transport)}`}
      className="flex h-[18px] min-w-56"
    >
      <span className="h-full bg-ks-purple" style={{ width: share(line.raw, max) }} />
      {line.duty === null ? (
        <span className={`h-full ${HATCH_CLASS}`} style={{ ...HATCH_STYLE, width: share(UNKNOWN_DUTY_WIDTH, max) }} />
      ) : (
        <span className="h-full bg-ks-purple-2" style={{ width: share(line.duty, max) }} />
      )}
      <span className="h-full bg-ks-grey-1" style={{ width: share(line.transport, max) }} />
    </div>
  );
}

export function landedColumns(max: number): ColumnDef<LandedLine, unknown>[] {
  return [
    {
      id: "offer",
      header: "Offer",
      cell: ({ row }) => (
        <>
          <OptionLink optionId={row.original.row.option_id} itemId={row.original.row.item_id} from="landed_cost_by_offer">
            {row.original.row.option_name}
          </OptionLink>
          {row.original.row.incoterm ? ` - ${row.original.row.incoterm}` : null}
        </>
      ),
    },
    { id: "breakdown", header: "Breakdown", cell: ({ row }) => <Breakdown line={row.original} max={max} /> },
    { id: "raw", header: "Raw", meta: { align: "right" }, cell: ({ row }) => f2(row.original.raw) },
    {
      id: "duty",
      header: "Duty",
      meta: { align: "right" },
      cell: ({ row }) => (row.original.duty === null ? <FlagChip kind="duty_unknown" /> : f2(row.original.duty)),
    },
    { id: "transport", header: "Transport", meta: { align: "right" }, cell: ({ row }) => f2(row.original.transport) },
    { id: "total", header: "Total", meta: { align: "right" }, cell: ({ row }) => <b>{f2(row.original.total)}</b> },
  ];
}

export function LandedByOfferBody({ rows, origin }: { rows: LandedRow[]; origin: string | null }) {
  const lines = byTotalDesc(landedLines(rows, origin));
  return (
    <>
      <LandedLegend />
      <div className="overflow-x-auto">
        <DataTable<LandedLine>
          columns={landedColumns(stackMax(lines))}
          rows={lines}
          page={{ pageIndex: 0, pageSize: Math.max(1, lines.length), rowCount: lines.length }}
          onPageChange={() => {}}
          density="catalogue"
          caption="Landed cost per offer"
        />
      </div>
    </>
  );
}

export function LandedByOfferPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const origin = pageFilter(search, "origin");
  const destination = pageFilter(search, "destination") ?? "UK";
  return (
    <VariantPage tab={tab}>
      <TabPage<Page<LandedRow>>
        tab={tab}
        areaName={areaName}
        extra={landedExtra(tab.tabKey, search)}
        isEmpty={(data) => landedLines(data.items, origin).length === 0}
        head={(data) => (
          <PageHead
            title={tab.label}
            sub={data?.items[0] ? `${data.items[0].variant} · landed in ${destination} · GBP per kg` : undefined}
          />
        )}
      >
        {(data) => <LandedByOfferBody rows={data.items} origin={origin} />}
      </TabPage>
    </VariantPage>
  );
}
